import { autoinject, computedFrom } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { Router, RouteConfig } from 'aurelia-router';
import * as moment from 'moment';
import 'moment-timezone';
import numeral from 'numeral';
import { AchToken } from '../../models/ach-token';
import { CardToken } from '../../models/card-token';
import { TemplateData } from '../../models/template-data';
import { State } from '../../services/global-state';
import { PayrazrService } from '../../services/payrazr';
import { PortalTemplateTypes } from '../../services/template-service';
import { Customer } from '../../models/customer';
import { PaymentPlanType } from '../../models/paymentPlanType';
import { MerchantConsumerPortalFeeSettings } from '../../models/merchant-consumer-portal-fee-settings';
import { PaymentPlanSettings } from '../../models/payment-plan-settings';
import { ApplePaySettings, ApplePayUtil, DigitalWalletPaymentDetails, DigitalWalletType } from '../../models/digital-wallet';
import { PaymentFeeType } from '../../models/hpfApi';

@autoinject
export class SetupRecurring {
	paymentInformation: any;
	account: any;
	customer: Customer;
	portfolioSettings: any;
	recurringSettings: any;
	numberOfRetries: any;
	balance: any;
	selectedInstallmentNumber: any;
	validationErrors: any;
	movingToConfirmationPage: any;
	isSubmitting = false;
	cardNumber: any;
	cardInfoKey: any;

	// for changed event
	paymentAmount: any = '';
	lastPaymentDate: any = '';
	selectedFrequency: any;
	firstPaymentDate: any;
	availableFrequencies: any;
	minFuture: any;
	maxFuture: any;
	maxLastFutureDays: any;
	hideTheIndefiniteIndicator: any;
	calendarDisplayMode: any;
	convenienceFeeDisclosure: any;
	recurringOptionsSelected: any;
	state: string = 'setup';
	confirmationTemplateData: TemplateData;
	confirmationTitle: string;
	totalServiceFee: any;
	paymentDate: any;
	transactionError: any;
	singleServiceFee: any;
	firstName: any;
	lastName: any;
	today: any;
	content: any;
	hashedContent: any;
	calculatedFeeAmount: number = 0;
	calculatedTotalAmount: number = 0;
	isDigitalWallet: boolean;
	merchantKey: number;
	merchantConsumerPortalFeeSettings: MerchantConsumerPortalFeeSettings = new MerchantConsumerPortalFeeSettings();
	paymentPlanSettings: PaymentPlanSettings = new PaymentPlanSettings();

	get isApplePay(): boolean {
		return this.isDigitalWallet && this.paymentInformation.digitalWalletSettings?.isApplePay();
	}

	lastPaymentDateRadioByDate = {
		id:1,
		description:'By Date'
	};

	lastPaymentDateRadioIndefinite = {
		id:2,
		description:'Indefinite'
	};

	lastPaymentDateOptions = [
		this.lastPaymentDateRadioByDate,
		this.lastPaymentDateRadioIndefinite
	];

	lastPaymentDateOptionSelected: number = 0;

	constructor(private router: Router, private globalState: State, private payrazrService: PayrazrService, private i18n: I18N) {
		this.firstPaymentDate = '';
		this.availableFrequencies = [];
		this.minFuture = 0;
		this.maxFuture = 60;
		this.maxLastFutureDays = 9999999;
		this.calendarDisplayMode = 0;
		this.selectedFrequency = '';
		this.validationErrors = [];
		this.today = moment.tz(this.globalState.business.businessTimezone).format('MM/DD/YYYY');
		this.confirmationTitle = this.i18n.i18next.t('completePlanSetUp', 'Please review the below information, enter the Account Holder Name EXACTLY AS IT APPEARS BELOW, and click "I Accept", in order to complete the plan set-up.');
	}

	activate(params) {
		this.account = this.globalState.accounts.find((element, index, array) => {
			return element.id === params.id;
		});

		this.customer = this.globalState.customer;

		if (this.globalState.isConsumerPortalGuarantorBillingEnabled) {
			this.globalState.associatedCustomers.forEach(customer => {
				customer.accounts.forEach(account => {
					if (this.account.customerAccountKey === account.customerAccountKey) {
						this.customer = customer;
					}
				});
			});
		}

		const currentPortfolio = this.globalState.business.fetchPortfolioSettings(this.account.portfolioName);
		// map recurring settings
		this.portfolioSettings = currentPortfolio.settings;
		this.recurringSettings = this.portfolioSettings.paymentTypes.recurring;
		this.numberOfRetries = parseInt(this.recurringSettings.numberOfRetriesForCardDeclines, 10);
		this.balance = this.account.balance;
		this.availableFrequencies = this.recurringSettings.paymentFrequencies;

		this.setMaxPaymentDates();
	}

	attached() {
		this.paymentPlanSettings.accountId = this.account.id;
		this.paymentPlanSettings.paymentPlanType = PaymentPlanType.Recurring;
		this.paymentPlanSettings.paymentFeeType = PaymentFeeType.PaymentPlan;

		const currentPortfolio = this.globalState.business.fetchPortfolioSettings(this.account.portfolioName);
		// map key and fee settings from merchant settings
		this.merchantKey = currentPortfolio.merchantSettings.merchantKey;
		this.merchantConsumerPortalFeeSettings.feesEnabled = currentPortfolio.merchantSettings.feeSettings.serviceFeeEnabled;
		this.merchantConsumerPortalFeeSettings.allowFeesOnPaymentPlans = currentPortfolio.merchantSettings.feeSettings.allowFeesOnPaymentPlans;
		this.merchantConsumerPortalFeeSettings.allowServiceFeesOnEveryTransaction = currentPortfolio.merchantSettings.feeSettings.allowServiceFeesOnEveryTransaction;
	}

	translateFrequency(frequencyId: number) {
		switch (frequencyId) {
			case 0:
				return this.i18n.i18next.t('selectAValue', 'Please select a value');
			case 1:
				return this.i18n.i18next.t('weekly', 'Weekly');
			case 2:
				return this.i18n.i18next.t('every2Weeks', 'Every 2 weeks');
			case 3:
				return this.i18n.i18next.t('every28Days', 'Every 28 Days');
			case 4:
				return this.i18n.i18next.t('monthly', 'Monthly');
			case 5:
				return this.i18n.i18next.t('eachMonth15', '15th of each month');
			case 6:
				return this.i18n.i18next.t('endOfMonth', 'End of month');
			case 7:
				return this.i18n.i18next.t('every2Months', 'Every 2 months');
		}
	}

	indefiniteLastPaymentChanged() {
		this.lastPaymentDate = '';
		this.lastPaymentDateOptionSelected = this.lastPaymentDateRadioIndefinite.id;
	}

	lastPaymentDateChanged() {
		if (this.lastPaymentDateOptionSelected !== this.lastPaymentDateRadioByDate.id) {
			this.lastPaymentDateOptionSelected = this.lastPaymentDateRadioByDate.id;
		}
	}

	paymentAmountChanged() {
		this.frequencyToDisplayMode();

		if (this.paymentAmount === '') { return; }

		try {
			this.paymentAmount = numeral(this.paymentAmount).format('0.00');
		}
		catch (error) {
			this.paymentAmount = '';
		}
	}

	selectedFrequencyChanged() {
		this.firstPaymentDate = '';
		this.frequencyToDisplayMode();
	}

	frequencyToDisplayMode() {
		this.calendarDisplayMode = 0;

		let indexofSelectedFrequency = parseInt(this.selectedFrequency, 10);
		if (indexofSelectedFrequency === 5) { this.midofEachMonthHandler(); }
		if (indexofSelectedFrequency === 6) { this.endOfMonthHandler(); }
	}

	midofEachMonthHandler() {
		this.calendarDisplayMode = 1;
		let newDate = new Date(moment.tz(this.globalState.business.businessTimezone).format('MM/DD/YYYY'));
		let theNumOfDate = newDate.getDate();
		let currentMidOfMonth = new Date(newDate.getFullYear(), newDate.getMonth(), 15);
		let nextMidOfMonth = new Date(newDate.getFullYear(), newDate.getMonth() + 1, 15);

		let midOfMonth = theNumOfDate <= 15 ? currentMidOfMonth : nextMidOfMonth;
		this.firstPaymentDate = moment(midOfMonth).format('MM/DD/YYYY');
	}

	endOfMonthHandler() {
		this.calendarDisplayMode = 2;
		let newDate = new Date(moment.tz(this.globalState.business.businessTimezone).format('MM/DD/YYYY'));
		let endOfMonth = new Date(newDate.getFullYear(), newDate.getMonth() + 1, 0);
		this.firstPaymentDate = moment(endOfMonth).format('MM/DD/YYYY');
	}

	get numberOfServiceFee() {
		// todo: need to create a new api call to get the exact number of service fee
		if (!this.selectedInstallmentNumber || this.selectedInstallmentNumber === 'Please select a value') {
			return 1;
		}
		return this.selectedInstallmentNumber.split(' ')[0];
	}

	get customerAccountKeys(): number[] {
		return [this.account.customerAccountKey];
	}

	get allowFeesOnPaymentPlans(): boolean {
		return this.merchantConsumerPortalFeeSettings.feesEnabled && this.merchantConsumerPortalFeeSettings.allowFeesOnPaymentPlans;
	}

	next(event: CustomEvent) {
		this.validationErrors = event.detail.errors;
		this.validate();
		if (this.validationErrors.length === 0) {
			this.paymentInformation = event.detail;
			this.paymentInformationFromForm();
			this.paymentPlanSettings.recurring = this.paymentInformation.recurring;
			this.cardNumber = this.paymentInformation.card ? this.paymentInformation.card.cardNumber : null;
			this.cardInfoKey = this.paymentInformation.card && this.paymentInformation.card.selectedPaymentMethod ? this.paymentInformation.card.selectedPaymentMethod.cardInfoKey : null;

			// map Apple Pay
			if (this.isDigitalWallet && this.paymentInformation.digitalWalletSettings?.isApplePay()) {
				this.setApplePaySettingsRecurringFields();
				this.getConfirmationTemplateData();
				this.showConfirmation();
			}
			else {
				this.payrazrService.calculateFee(event.detail.card ? true : false, this.account.portfolioName, event.detail.zipCode, event.detail.amount, this.cardNumber, this.cardInfoKey, this.customerAccountKeys, PaymentPlanType.Recurring)
					.then(result => {
						this.paymentInformation.firstPaymentFee = result.entity.fee;
						let feeOnSubsequentPayments = this.merchantConsumerPortalFeeSettings.allowServiceFeesOnEveryTransaction ? result.entity.fee : 0;
						this.paymentInformation.subsequentPaymentFee = feeOnSubsequentPayments;
						this.getConfirmationTemplateData();
						this.showConfirmation();
					})
					.catch(error => {
						alert(this.i18n.i18next.t('unableToCommunicateWithServer', 'Unable to communicate with the server. Please try again.'));
					});
			}
		}
	}

	setApplePaySettingsRecurringFields(): void {
		if (!this.paymentInformation.digitalWalletSettings?.isApplePay()) {
			return;
		}

		const applePaySettings: ApplePaySettings = this.paymentInformation.digitalWalletSettings as ApplePaySettings;

		// recurring requests are set to pending to display 'amount pending' text in modal
		applePaySettings.totalType = 'pending';
		applePaySettings.recurringAmount = this.paymentAmount;
		applePaySettings.recurringLabel = '';
		applePaySettings.paymentDescription = this.i18n.i18next.t('setUpRecurringPaymentPlan', 'Set Up A Recurring Payment Plan');
		applePaySettings.paymentTiming = 'recurring';
		applePaySettings.recurringPaymentStartDate = new Date(this.firstPaymentDate);
		applePaySettings.recurringPaymentIntervalUnit = ApplePayUtil.getRecurringPaymentDateUnit(this.selectedFrequency);
		applePaySettings.recurringPaymentIntervalCount = ApplePayUtil.getRecurringPaymentIntervalCount(this.selectedFrequency);
		applePaySettings.billingAgreement = ApplePayUtil.getBillingAgreement(this.merchantConsumerPortalFeeSettings, this.i18n);
		applePaySettings.mangementURL = this.getViewInstallmentUrl();

		if (this.paymentInformation.recurring.lastPaymentDate) {
			applePaySettings.recurringPaymentEndDate = new Date(this.paymentInformation.recurring.lastPaymentDate);
		}

		this.paymentInformation.digitalWalletSettings = applePaySettings;
	}

	getViewInstallmentUrl(): string {
		let urlSplit: string[] = window.location.href.split('/');
		urlSplit.pop();
		return urlSplit.join('/') + '/view';
	}

	showConfirmation() {
		this.state = 'confirmation';
	}

	hideConfirmation() {
		this.state = 'setup';
	}

	getConfirmationTemplateData() {
		this.totalServiceFee = 0;

		let customObject: any = {
			paymentAmount : numeral(this.paymentInformation.recurring.paymentAmount).format('($0,0.00)'),
			accountNumberLabel : this.globalState.business.portalSettings.accountNumberLabel,
			customerAccountNumber: this.account.referenceId,
			accountNumber: this.account.referenceId, // backward compatibility
			customerAccountName: this.account.name,
			balance: numeral(this.account.balance).format('($0,0.00)'),
			firstPaymentFee: this.firstPaymentFee,
			subsequentPaymentFee: this.subsequentPaymentFee,
			frequency : this.paymentInformation.recurring.frequency.name,
			total : numeral(this.calculatedTotalAmount).format('($0,0.00)'),
			merchantName : this.globalState.business.name,
			paymentScheduleTable: '',
			numberOfRetries: this.paymentInformation.recurring.numberOfRetriesForCardDeclines,
			accountName: this.customer.name,
			zipCode: this.isDigitalWallet ? 'Will Display in Apple Pay' : this.paymentInformation.zipCode,
			paymentMethod: this.paymentMethod,
			customerAccountLocationName: (this.account.bucket) ? this.account.bucket.displayName : '',
			customerAccountLocationReferenceId: (this.account.bucket) ? this.account.bucket.referenceId : '',
			billingPhone: this.globalState.customer.mobilePhone,
			billingEmail: this.globalState.customer.email,
			originalLenderName: this.account.originalLenderName,
			repaymentType: this.account.repaymentType
		};

		const merchantKey = this.globalState.business.getMerchantKeyAssociatedToPortfolioByPortfolioName(this.account.portfolioName);
		this.confirmationTemplateData = new TemplateData(customObject, merchantKey);
	}

	get firstPaymentFee(): string {
		return this.isDigitalWallet && this.allowFeesOnPaymentPlans
			? 'Will Display in Apple Pay'
			: numeral(this.paymentInformation.firstPaymentFee).format('($0,0.00)');
	}

	get subsequentPaymentFee(): string {
		return this.isDigitalWallet && this.allowFeesOnPaymentPlans && this.merchantConsumerPortalFeeSettings.allowServiceFeesOnEveryTransaction
			? 'Will Display in Apple Pay'
			: numeral(this.paymentInformation.subsequentPaymentFee).format('($0,0.00)');
	}

	get paymentMethod() {
		if (this.paymentInformation.isWallet) {
			if (this.paymentInformation.card && this.paymentInformation.card.selectedPaymentMethod) {
				return this.i18n.i18next.t('walletPaymentMethods', { defaultValue: `${this.paymentInformation.card.selectedPaymentMethod.cardType} ending ${this.paymentInformation.card.selectedPaymentMethod.last4ofCard}`, paymentMethodTypes: this.paymentInformation.card.selectedPaymentMethod.cardType, accountNumber: this.paymentInformation.card.selectedPaymentMethod.last4ofCard });
			}
			else if (this.paymentInformation.check && this.paymentInformation.check.selectedPaymentMethod) {
				return this.i18n.i18next.t('walletPaymentMethods', { defaultValue: `${this.paymentInformation.check.selectedPaymentMethod.achAccountType} ending ${this.paymentInformation.check.selectedPaymentMethod.last4ofAccount}`, paymentMethodTypes: this.paymentInformation.check.selectedPaymentMethod.achAccountType, accountNumber: this.paymentInformation.check.selectedPaymentMethod.last4ofAccount });
			}
		}
		else {
			if (this.paymentInformation.isCardPayment) {
				return this.paymentInformation.card.description;
			}
			if (this.paymentInformation.isCheckPayment) {
				return this.paymentInformation.check.description;
			}
			if (this.paymentInformation.isDigitalWallet) {
				return this.paymentInformation.digitalWalletSettings.getDigitalWalletName();
			}
		}
	}

	submit($event: CustomEvent) {
		this.paymentDate = moment(moment.tz(this.globalState.business.businessTimezone).format('MM/DD/YYYY'));

		return this.handleSaveToWallet()
			.then(response => {
				// TODO: Throw an error message specifying that the payment
				// method could not be added to the wallet.
				if (!response.isSuccessful) {
					return;
				}

				this.paymentInformation.savedPaymentMethodToWallet = !!response.entity;

				if (this.paymentInformation.savedPaymentMethodToWallet) {
					if (!!this.paymentInformation.card) {
						this.paymentInformation.card.cardInfoKey = response.entity.cardInfoKey;
					} else if (!!this.paymentInformation.check) {
						this.paymentInformation.check.checkInfoKey = response.entity.checkInfoKey;
					}
				}

				return this.callApiBasedOnPaymentMechanism($event.detail);
			})
			.then(result => {
				if (result.response.isSuccessful) {
					this.paymentInformation.planId = result.response.planId;
					this.paymentInformation.merchantKey = result.response.merchantKey;
					this.paymentInformation.portalTemplateType = this.isCardPayment()
						? PortalTemplateTypes.CreditCardRecurringPaymentPlanReceipt
						: PortalTemplateTypes.CheckRecurringPaymentPlanReceipt;
					this.paymentInformation.savedPaymentMethodToWallet = result.response.savedPaymentMethodToWallet;
				}
				else {
					this.paymentInformation.portalTemplateType = PortalTemplateTypes.TransactionFailed;
					this.paymentInformation.savedPaymentMethodToWallet = result.response.savedPaymentMethodToWallet;
					this.paymentInformation.resultCode = result.response.resultCode;
					this.paymentInformation.resultMessage = result.response.message;
				}

				if (this.isApplePay) {
					const isSuccessful: boolean = result.response.isSuccessful;
					ApplePayUtil.completeSession($event, isSuccessful);
				}
			})
			.then(() => {
				this.navigateToReceipt();
			})
			.catch(error => {
				if (this.isDigitalWallet) {
					const isSuccessful: boolean = false;
					ApplePayUtil.completeSession($event, isSuccessful);
				}

				this.isSubmitting = false;
				this.transactionError = error;
			});
	}

	navigateToReceipt(): void {
		this.isSubmitting = false;
		const receiptRoute: RouteConfig = this.router.routes.find(x => x.name === 'recurring/recurring-receipt');
		receiptRoute.paymentInformation = this.paymentInformation;
		this.router.navigateToRoute(receiptRoute.name, { id: this.account.id });
	}

	handleSaveToWallet(): Promise<any> {
		let saveToWallet = this.paymentInformation.addPaymentMethodToWalletIsSelected;

		if (saveToWallet
			&& this.globalState.isConsumerPortalGuarantorBillingEnabled) {
			this.paymentInformation.addPaymentMethodToWalletIsSelected = false;
		}

		if (!saveToWallet
			|| !this.globalState.isConsumerPortalGuarantorBillingEnabled) {
			return new Promise(resolve => {
				resolve({
					isSuccessful: true
				});
			});
		}

		let customerId: string = this.globalState.customer.id;

		// Use the customer.id of the customer in context.
		if (this.globalState.isConsumerPortalGuarantorBillingEnabled) {
			this.globalState.associatedCustomers.forEach(customer => {
				customer.accounts.forEach(account => {
					if (this.account.customerAccountKey === account.customerAccountKey) {
						customerId = customer.id;
					}
				});
			});
		}

		if (!!this.paymentInformation.card) {
			let cardToken = new CardToken();
			cardToken.cardholderName = this.paymentInformation.name;
			cardToken.cardNumber = this.paymentInformation.card.cardNumber;
			cardToken.expirationDate = this.paymentInformation.card.expirationDate;
			cardToken.zipCode = this.paymentInformation.zipCode;

			return this.payrazrService.addCardPaymentMethod(cardToken, false, customerId);
		} else if (!!this.paymentInformation.check) {
			let achToken = new AchToken();
			achToken.accountName = this.paymentInformation.name;
			achToken.accountNumber = this.paymentInformation.check.accountNumber;
			achToken.aba = this.paymentInformation.check.transitNumber;
			achToken.accountType = this.paymentInformation.check.accountType;
			achToken.addressLine1 = this.paymentInformation.check.addressLine1;
			achToken.zipCode = this.paymentInformation.zipCode;

			return this.payrazrService.addAchPaymentMethod(achToken, false, customerId);
		}
	}

	callApiBasedOnPaymentMechanism(submitEvent: any) {
		let isCardPayment = this.isCardPayment();
		let paymentDetails = this.getPaymentDetailsForPlanSubmission(submitEvent);
		let retries = isCardPayment ? this.paymentInformation.recurring.numberOfRetriesForCardDeclines : 0;

		if (this.isDigitalWallet) {
			this.singleServiceFee = submitEvent.calculatedServiceFee;
		}

		return this.payrazrService.createRecurringPaymentPlan(
			isCardPayment,
			this.account,
			this.paymentInformation.recurring.paymentAmount,
			this.paymentInformation.recurring.firstPaymentDate,
			this.paymentInformation.recurring.lastPaymentDate,
			this.paymentInformation.firstPaymentFee,
			this.paymentInformation.subsequentPaymentFee,
			this.paymentInformation.name,
			this.paymentInformation.addressLine1,
			this.paymentInformation.billingCity,
			this.paymentInformation.billingState,
			this.paymentInformation.billingPhone,
			this.paymentInformation.billingEmail,
			this.paymentInformation.zipCode,
			this.totalServiceFee,
			this.singleServiceFee,
			this.paymentInformation.transactionDescription,
			this.paymentInformation.isThirdPartyPayment,
			paymentDetails,
			retries,
			this.paymentInformation.recurring.frequency.id,
			this.firstName,
			this.lastName,
			this.today, // this.signedDate,
			submitEvent.content,
			submitEvent.hashedContent,
			this.paymentInformation.addPaymentMethodToWalletIsSelected,
			null,
			this.isDigitalWallet);
	}

	getPaymentDetailsForPlanSubmission(submitEvent: any): any {
		if (this.isDigitalWallet && submitEvent.digitalWalletType === DigitalWalletType.ApplePay) {
			return this.getDigitalWalletPaymentDetailsFromSubmitEvent(submitEvent);
		}
		return this.isCardPayment() ? this.paymentInformation.card : this.paymentInformation.check;
	}

	getDigitalWalletPaymentDetailsFromSubmitEvent(submitEvent: any): DigitalWalletPaymentDetails {
		if (submitEvent.digitalWalletType === DigitalWalletType.ApplePay) {
			const applePayPayment: ApplePayJS.ApplePayPayment = submitEvent.payment;
			const applePayPaymentToken = JSON.stringify(applePayPayment.token);
			let digitalWalletPaymentDetails: DigitalWalletPaymentDetails = {
				authenticationPaymentToken: btoa(applePayPaymentToken),
				digitalWalletType: DigitalWalletType.ApplePay,
				isDebitCard: applePayPayment.token.paymentMethod.type === 'debit',
				isCreditCard: applePayPayment.token.paymentMethod.type === 'credit',
				accountName: applePayPayment.billingContact?.givenName + ' ' + applePayPayment.billingContact?.familyName,
				zipCode: applePayPayment.billingContact?.postalCode ?? '',
			};

			return digitalWalletPaymentDetails;
		}
	}

	showErrorMessage(message: string) {
		alert(message);
	}

	isCardPayment(): boolean {
		return !!this.paymentInformation.card || this.isDigitalWalletCard();
	}

	isDigitalWalletCard(): boolean {
		return this.isDigitalWallet && (this.paymentInformation.digitalWalletSettings?.isApplePay() ?? false);
	}

	paymentInformationFromForm() {
		let selectedFrequency = this.selectedFrequency;
		let frequency = this.recurringSettings.paymentFrequencies.find(x => x.id == selectedFrequency);
		/*collect recurring details to paymentinformation*/
		this.paymentInformation.recurring = {
			paymentAmount: this.paymentAmount,
			firstPaymentDate: this.firstPaymentDate,
			lastPaymentDate: this.lastPaymentDate,
			frequency,
			numberOfRetriesForCardDeclines: this.recurringSettings.numberOfRetriesForCardDeclines
		};
	}

	@computedFrom('availableFrequencies')
	get availableFrequencyIds(): number[] {
		let opts: number[] = this.availableFrequencies.map(freq => freq.id);
		return [0, ...opts]; // prepend 'Please select a value' option
	}

	@computedFrom('availableFrequencyIds')
	get availableFrequencyDescriptions(): string[] {
		return this.availableFrequencyIds.map(id => this.translateFrequency(id));
	}

	validate() {
		if (this.paymentAmount === '') {
			this.validationErrors.push(this.i18n.i18next.t('selectPaymentAmount', 'Please select a payment amount'));
		}

		if (this.selectedFrequency === '0') {
			this.validationErrors.push(this.i18n.i18next.t('selectFrequencyOfRecurringPayments', 'Please select the frequency of recurring payments'));
		}

		this.validatePaymentDates();
	}

	validatePaymentDates() {
		let firstPaymentDate = moment(this.firstPaymentDate, 'MM/DD/YYYY');
		let lastPaymentDate = moment(this.lastPaymentDate, 'MM/DD/YYYY');

		let minFuture = moment(moment.tz(this.globalState.business.businessTimezone).format('MM/DD/YYYY')).add(this.minFuture, 'd').startOf('day');
		let maxFuture = moment(moment.tz(this.globalState.business.businessTimezone).format('MM/DD/YYYY')).add(this.maxFuture, 'd').endOf('day');
		let maxLastFutureDays = moment(moment.tz(this.globalState.business.businessTimezone).format('MM/DD/YYYY')).add(this.maxLastFutureDays, 'd').endOf('day');

		if (!firstPaymentDate.isValid()) {
			this.validationErrors.push(this.i18n.i18next.t('selectValidFirstPaymentDate', 'Please select a valid first payment date'));
		}

		if (firstPaymentDate < moment(moment.tz(this.globalState.business.businessTimezone).format('MM/DD/YYYY')).startOf('day')) {
			this.validationErrors.push(this.i18n.i18next.t('firstPaymentDateCannotBeInPast', 'First payment date cannot be in the past'));
		}

		if (firstPaymentDate > maxFuture) {
			this.validationErrors.push(this.i18n.i18next.t('paymentDateCannotBeMoreThan', { defaultValue: 'Payment date cannot be more than ' + this.maxFuture + ' day' + (this.maxFuture > 1 ? 's' : '') + ' from now', future: this.maxFuture, sAddition: this.maxFuture > 1 ? 's' : '' }));
		}

		if (lastPaymentDate > maxLastFutureDays) {
			this.validationErrors.push(this.i18n.i18next.t('paymentDateCannotBeMoreThan', { defaultValue: 'Payment date cannot be more than ' + this.maxLastFutureDays + ' day' + (this.maxLastFutureDays > 1 ? 's' : '') + ' from now', future: this.maxLastFutureDays, sAddition: this.maxLastFutureDays > 1 ? 's' : '' }));
		}

		if (this.lastPaymentDateOptionSelected === this.lastPaymentDateRadioByDate.id) {
			if (!lastPaymentDate.isValid()) {
				this.validationErrors.push(this.i18n.i18next.t('selectValidLastPaymentDate', 'Please select a valid last payment date'));
			}

			if (lastPaymentDate < firstPaymentDate) {
				this.validationErrors.push(this.i18n.i18next.t('lastPaymentDateCannotBeBeforeFirst', 'Last payment date cannot be before first payment date'));
			}
		}
	}

	goBack() {
		this.router.navigateToRoute('secure');
			this.validationErrors = [];
			return;
	}

	reset() {
		this.validationErrors = new Array();
	}

	setMaxPaymentDates() {
		this.maxFuture = this.recurringSettings.maxFirstRecurringPaymentFutureDays;
		this.lastPaymentDateOptionSelected = this.lastPaymentDateRadioByDate.id;
		this.maxLastFutureDays = this.recurringSettings.maxLastRecurringPaymentFutureDays || this.maxLastFutureDays;
		this.hideTheIndefiniteIndicator = this.recurringSettings.hideTheIndefiniteIndicator;
	}
}
